import Cookies from 'js-cookie';
import type { CookieAttributes } from 'js-cookie';

const DOMAIN = '.be-my-media.com';

/**
 * CF7: Add cookies to Forms fields
 * https://github.com/js-cookie/js-cookie
 */

// 1 - Generic helper functions

/**
 * @returns {boolean} - Whether this is a local setup
 */
function isPre0(): boolean {
  return window.location.host.indexOf('pre0.be-my-media.com') === 0;
}

/**
 *
 * @param {string} utmVar - UTM parameter value
 * @param {boolean} url - If this is an URL
 * @returns {string} - The clean value
 */
function cleanParam(utmVar: string, url: boolean): string {
  let cleanVar = utmVar.trim()
    .replace('|', '-')
    .replace(/;/g, '')
    .replace(/'/g, '')
    .replace(/"/g, '')
    .replace(/,/g, '')
    .replace(/(<([^>]+)>)/gi, '')
    .replace(/</g, '')
    .replace(/>/g, '')
    .replace(/\$/g, '')
    .replace(/@/g, '');

  if (url !== true) {
    cleanVar = cleanVar
      .replace(/\?/g, '')
      .replace(/&/g, '')
      .replace(/=/g, '')
      .replace(/#/g, '')
      .replace(/\//g, '')
      .replace(/:/g, '');
  }

  return cleanVar;
}

/**
 *
 * @param {string | false} utmSource - UTM source
 * @param {string | false} utmCampaign - UTM campaign
 * @returns {false | number} - If this is a Sendinblue campaign, the life time of the cookie
 */
function isSibCampaign(
  utmSource: string | false,
  utmCampaign: string | false,
): false | number {
  let sibCampaignReturn: false | number = false;

  if (utmSource === 'sib' && utmCampaign && utmCampaign.substring(0, 5) === 'sib__') {
    let sibCampaignArray = [];
    sibCampaignArray = utmCampaign.split('__');
    let sibCampaignLifeTime = Number(sibCampaignArray[1]);

    if (sibCampaignArray.length === 3 && !Number.isNaN(sibCampaignLifeTime)) {
      sibCampaignLifeTime = Number(sibCampaignLifeTime);
      if (sibCampaignLifeTime > 365) {
        sibCampaignLifeTime = 365;
      }

      sibCampaignReturn = sibCampaignLifeTime;
    }
  }

  return sibCampaignReturn;
}

/**
 *
 * @param {boolean} isUtm - Whether this is for UTM cookies
 * @param {Array<Array<(string | false)>>} utmParams - UTM parameters
 * @returns {CookieAttributes} - Cookie Attributes
 */
function getCookieArgs(
  isUtm: boolean,
  utmParams?: Array<Array<(string | false)>>,
): CookieAttributes {
  let cookieLifeTime = 0;
  if (isUtm && typeof utmParams !== 'undefined') {
    const isSib = isSibCampaign(utmParams[1][1], utmParams[2][1]);

    if (isSib !== false) {
      cookieLifeTime = isSib;
    }
  }

  return {
    expires: cookieLifeTime,
    path: '/',
    domain: DOMAIN,
    secure: !isPre0(),
    sameSite: 'strict',
  };
}

/**
 *
 * @param {string} cookieName - Names of the cookie
 * @returns {boolean} - Whether the cookie is set
 */
function isCookieSet(cookieName: string): boolean {
  return typeof Cookies.get(cookieName) !== 'undefined' && Cookies.get(cookieName) !== '';
}

/**
 *
 * @param {string[]} cookiesNames - Names of the cookies
 * @returns {boolean} - Whether cookies are set
 */
function areCookiesSet(cookiesNames: string[]): boolean {
  let cookiesAreSet = false;
  cookiesNames.forEach((cookieName) => {
    if (isCookieSet(cookieName)) {
      cookiesAreSet = true;
    }
  });
  return cookiesAreSet;
}

// 2 - UTM Cookies

/**
 *
 * @param {string} theParameterName - Name of the GET parameter to find
 * @returns {false | string} - Value of the parameter on success
 */
function findTheGetParameter(theParameterName: string): false | string {
  let pmresult: false | string = false;
  let pmtmp = [];
  const pmitems = window.location.search.substr(1).split('&');
  pmitems.forEach((pm) => {
    pmtmp = pm.split('=');
    const [paramName, paramValue] = pmtmp;
    if (paramName === theParameterName) {
      pmresult = paramValue;
    }
  });
  return pmresult;
}

/**
 * @returns {string[]} - Names of UTM parameters
 */
function getUtmNamesArray(): string[] {
  return ['utm_medium', 'utm_source', 'utm_campaign', 'utm_content', 'utm_term'];
}

/**
 * @returns {string[]} - Landing page cookies names
 */
function getUtmCookieNames(): string[] {
  return getUtmNamesArray().map((utm) => `bemymedia_utmcookie_${utm}`);
}

/**
 * @returns {boolean} - Whether UTM cookies are set
 */
function areUtmCookiesSet(): boolean {
  const utmCookies = getUtmCookieNames();
  return areCookiesSet(utmCookies);
}

/**
 * @returns {boolean} - Success
 */
function setUtmCookies(): boolean {
  if (areUtmCookiesSet()) {
    return false;
  }
  const utmParams = getUtmNamesArray().map((utm) => [
    `bemymedia_utmcookie_${utm}`, findTheGetParameter(utm),
  ]);

  const args = getCookieArgs(true, utmParams);

  utmParams.forEach(([cookieName, utmValue]) => {
    if (utmValue && cookieName) {
      Cookies.set(cookieName, cleanParam(`${utmValue}`, false), args);
    }
  });
  return true;
}

// 3 - Landing page Cookies

/**
 * @returns {string[]} - Landing page cookies names
 */
function getLandingCookieNames(): string[] {
  return ['bemymedia_cookie_landing_page', 'bemymedia_cookie_landing_cat'];
}

/**
 * @returns {boolean} - Whether landing page cookies are set
 */
function areLandingCookiesSet(): boolean {
  const utmCookies = getLandingCookieNames();
  return areCookiesSet(utmCookies);
}

/**
 * @returns {boolean} - Success
 */
function setLandingCookies(): boolean {
  if (areLandingCookiesSet()) {
    return false;
  }

  const currentUrl = `${window.location.origin}${window.location.pathname}`;
  const landingCat = 'Site be. my media';

  const args = getCookieArgs(false);

  Cookies.set('bemymedia_cookie_landing_page', cleanParam(currentUrl, true), args);
  Cookies.set('bemymedia_cookie_landing_cat', cleanParam(landingCat, false), args);

  return true;
}

/**
 * Remove Google Analytics URL parameters for cross-domain tracking
 *
 * @param {number} i - Recursion level
 * @returns {boolean} - Success
 */
function cleanUrlFromGaParams(i: number): boolean {
  if (window.location.search.includes('_ga=') || window.location.search.includes('_gl=')) {
    if (
      (i > 20) // recursion capped at 20
    || (
      typeof window.ga !== 'undefined'
      && typeof window.ga.getAll === 'function'
      && window.ga.getAll().length
      && typeof window.ga.getAll()[0].get('clientId') === 'string'
    )
    ) {
      // push canonical href into history
      const canonicalTag = document.querySelector("link[rel='canonical']") as HTMLLinkElement;
      if (canonicalTag && Object.prototype.hasOwnProperty.call(canonicalTag, 'href')) {
        window.history.replaceState({}, '', canonicalTag.href);
        return true;
      }
      // fallback (no canonical)
      window.history.replaceState({}, '', window.location.pathname);
      return true;
    }
    setTimeout(cleanUrlFromGaParams, 500, i + 1);
  }
  return true;
}

// 4 - Main
window.addEventListener('load', () => {
  // Set cookies for UTMs and landing page
  setUtmCookies();
  setLandingCookies();

  // Remove GA params to show a clean URL
  cleanUrlFromGaParams(0);
});
